import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map, take } from "rxjs/operators";
import { ParcelPermitService } from "src/app/shared/generated/api/parcel-permit.service";
import { ParcelPermitWorkflowProgressDtoSteps } from "src/app/shared/generated/model/parcel-permit-workflow-progress-dto-steps";
import { AlertService } from "src/app/shared/services/alert.service";
import { Alert } from "src/app/shared/models/alert";
import { AlertContext } from "src/app/shared/models/enums/alert-context.enum";

/**
 * S3 — blocks direct navigation to an "Enter Permit Details" step while the "Review Parcel Data"
 * prerequisites are not all complete (e.g. a section is still Incomplete or NotReviewed). The
 * workflow nav already disables these steps visually; this guard closes the direct-URL hole and
 * surfaces the same message the legacy app showed.
 */
interface StepRouteMeta {
    stepKey: keyof ParcelPermitWorkflowProgressDtoSteps;
    sectionName: string;
}

const ENTER_PERMIT_DETAILS_STEPS: Record<string, StepRouteMeta> = {
    "application-overview": { stepKey: "ApplicationOverview", sectionName: "Application Overview" },
    "proposed-coverage": { stepKey: "ProposedCoverage", sectionName: "Coverage" },
    "proposed-development-rights": { stepKey: "ProposedDevelopmentRights", sectionName: "Development Rights" },
    "deed-restrictions-and-violations": { stepKey: "DeedRestrictionAndViolation", sectionName: "Restrictions / Violations" },
    "fees-and-security": { stepKey: "FeesAndSecurity", sectionName: "Fees / Security" },
    "upload-documents": { stepKey: "UploadDocuments", sectionName: "Upload Documents" },
};

@Injectable({ providedIn: "root" })
export class EnterPermitDetailsPrerequisiteGuard implements CanActivate {
    constructor(
        private parcelPermitService: ParcelPermitService,
        private router: Router,
        private alertService: AlertService
    ) {}

    canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
        const path = route.routeConfig?.path ?? "";
        const meta = ENTER_PERMIT_DETAILS_STEPS[path];
        const parcelPermitID = this.resolveParcelPermitID(route);

        // No prerequisite gating for unknown steps or when there is no permit id (defensive).
        if (!meta || !parcelPermitID) {
            return of(true);
        }

        return this.parcelPermitService.getWorkflowProgressParcelPermit(parcelPermitID).pipe(
            take(1),
            map((progress) => {
                const step = progress?.Steps?.[meta.stepKey];
                if (!step?.Disabled) {
                    return true;
                }
                this.alertService.pushAlert(
                    new Alert(
                        `Unable to edit section ${meta.sectionName} until all prerequisite sections have been reviewed.`,
                        AlertContext.Danger
                    )
                );
                return this.router.createUrlTree(["/parcel-permits", parcelPermitID, "edit", "basics"]);
            }),
            catchError(() => of(true))
        );
    }

    private resolveParcelPermitID(route: ActivatedRouteSnapshot): number | null {
        let current: ActivatedRouteSnapshot | null = route;
        while (current) {
            const id = current.paramMap.get("parcelPermitID");
            if (id) {
                return Number(id);
            }
            current = current.parent;
        }
        return null;
    }
}
